import { useRecoilValue } from "recoil";
import { Highlight } from "../types/Highlight";
import { currentOpenedArticleIdState } from "../atoms/rootAtom";

export enum HighlightType {
	TaskReading = "task-reading-highlighted",
	TaskQA = "task-qa-highlighted",
	Quotation = "quotation-highlighted",
}

function isScrollable(el: Element) {
	const style = window.getComputedStyle(el);

	const oy = style.overflowY;
	const ox = style.overflowX;
	const h = (el as HTMLElement).scrollHeight > (el as HTMLElement).clientHeight;
	const w = (el as HTMLElement).scrollWidth > (el as HTMLElement).clientWidth;

	return ((oy === "auto" || oy === "scroll") && h) || ((ox === "auto" || ox === "scroll") && w);
}

function getScrollableAncestor(el: HTMLElement | null): HTMLElement {
	let node: HTMLElement | null = el?.parentElement || null;

	while (node && node !== document.body && node !== document.documentElement) {
		if (isScrollable(node)) {
            return node;
        }

		node = node.parentElement;
	}

	return (document.scrollingElement as HTMLElement) || document.documentElement;
}

function elementTopWithin(el: HTMLElement, container: HTMLElement) {
	const er = el.getBoundingClientRect();
	const cr = container.getBoundingClientRect();

	return er.top - cr.top + (container.scrollTop || 0);
}

function isInView(
	el: HTMLElement,
	container: HTMLElement,
	offset = 68,
	bottomPadding = 24,
	tolerance = 8
) {
	const top = elementTopWithin(el, container);
	const bottom = top + el.offsetHeight;
	const viewTop = (container.scrollTop || 0) + offset;
	const viewBottom = (container.scrollTop || 0) + container.clientHeight - bottomPadding;

	// Allow a small tolerance so we don't "nudge" the scroll when already near the edge
	return bottom >= viewTop - tolerance && top <= viewBottom + tolerance;
}

function scrollToElementWithOffset(el: HTMLElement, offset = 68, smooth = true) {
	const container = getScrollableAncestor(el);
	const targetTop = elementTopWithin(el, container) - offset;
	const maxScroll = container.scrollHeight - container.clientHeight;
	const clamped = Math.max(0, Math.min(targetTop, maxScroll));
	const opts: ScrollToOptions = { top: clamped, behavior: smooth ? "smooth" : "auto" };

	if (
		container === document.documentElement ||
		container === document.body ||
		container === (document.scrollingElement as HTMLElement)
	) {
		window.scrollTo(opts);
	} else {
		container.scrollTo(opts);
	}
}

const useHighlights = () => {
	const currentOpenedArticleId = useRecoilValue(currentOpenedArticleIdState);

	const clearHighlights = (highlightType: HighlightType[] | HighlightType) => {
		if (Array.isArray(highlightType)) {
			highlightType.forEach(clearHighlights);
			return;
		}

		const targetSentences = Array.from(document.querySelectorAll(`span.${highlightType}`));

		targetSentences.forEach(
			(s) => s instanceof HTMLElement && s.classList.remove(highlightType)
		);
	};

	const highlightSentences = (
		highlight: Highlight,
		highlightType: HighlightType,
		noScroll?: boolean
	) => {
		clearHighlights(highlightType);

		if (!highlight) {
            return;
        }

		const indices = highlight[currentOpenedArticleId] as number[];
		if (!indices || indices.length === 0) {
            console.warn(`No indices found for highlight type ${highlightType}. Cannot apply highlights.`);
            return;
        }

		// Apply highlight classes
		indices.forEach((idx) => {
			const nodes = Array.from(document.querySelectorAll(`span[s_index="${idx}"]`));
			nodes.forEach((n) => n instanceof HTMLElement && n.classList.add(highlightType));
		});

		if (noScroll) {
            return;
        }

		// Ensure the FIRST highlighted sentence is in view
		const firstIndex = indices[0];
		const firstEl = document.querySelector(
			`span[s_index="${firstIndex}"]`
		) as HTMLElement | null;

		if (!firstEl) {
            console.warn(`No element found for first index ${firstIndex}. Cannot scroll.`);
            return;
        }

		const container = getScrollableAncestor(firstEl);
		if (!isInView(firstEl, container, 68, 24)) {
			scrollToElementWithOffset(firstEl, 68, true);
		} else {
            console.log(`First highlighted sentence ${firstIndex} is already in view, no scroll needed.`);
        }
	};

	return { clearHighlights, highlightSentences };
};

export default useHighlights;